/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessUpdateDirectoryReviewSettings } from "../funcs/accessUpdateDirectoryReviewSettings.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DirectoryReviewSettings } from "../models/components/directoryreviewsettings.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateDirectoryReviewSettingsRequest,
  UpdateDirectoryReviewSettingsSecurity,
} from "../models/operations/updatedirectoryreviewsettings.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateDirectoryReviewSettingsMutationVariables = {
  request: UpdateDirectoryReviewSettingsRequest;
  security?: UpdateDirectoryReviewSettingsSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateDirectoryReviewSettingsMutationData = DirectoryReviewSettings;

export type UpdateDirectoryReviewSettingsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateDirectoryReviewSettings access
 *
 * @remarks
 * Turn the organization's directory review emails on or off. Pending items stay listed in the dashboard either way, and webhooks are unaffected.
 */
export function useUpdateDirectoryReviewSettingsMutation(
  options?: MutationHookOptions<
    UpdateDirectoryReviewSettingsMutationData,
    UpdateDirectoryReviewSettingsMutationError,
    UpdateDirectoryReviewSettingsMutationVariables
  >,
): UseMutationResult<
  UpdateDirectoryReviewSettingsMutationData,
  UpdateDirectoryReviewSettingsMutationError,
  UpdateDirectoryReviewSettingsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateDirectoryReviewSettingsMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateDirectoryReviewSettings(): MutationKey {
  return ["@gram/client", "access", "updateDirectoryReviewSettings"];
}

export function buildUpdateDirectoryReviewSettingsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateDirectoryReviewSettingsMutationVariables,
  ) => Promise<UpdateDirectoryReviewSettingsMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateDirectoryReviewSettings(),
    mutationFn: function updateDirectoryReviewSettingsMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateDirectoryReviewSettingsMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessUpdateDirectoryReviewSettings(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
